import { FormEvent, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { useN8nWebhook } from '../../hooks/useN8nWebhook';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { n8nOriginFromUrl } from '../../lib/analysis-models';
import { todayLocalIso } from '../../lib/date';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';

const todayIso = todayLocalIso;

// Prefer the shared VITE_N8N_URL origin + the fixed /webhook/create-email path
// (so no extra var is needed — it's the same server as analysis runs); allow an
// explicit VITE_N8N_CREATE_EMAIL_URL override for a non-standard path.
function createEmailUrl(): string | null {
  const explicit = import.meta.env.VITE_N8N_CREATE_EMAIL_URL as string | undefined;
  if (explicit) return explicit;
  const origin = n8nOriginFromUrl(import.meta.env.VITE_N8N_URL);
  return origin ? `${origin}/webhook/create-email` : null;
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * Kicks off the n8n tips-email generation workflow (old admin's
 * EmailGenerationForm), passing free-text context + the target date. Lives at
 * the top of the Tips Email tab so drafting and reviewing sit together.
 */
export function GenerateTipsEmailPanel() {
  const { triggerWebhook, isLoading } = useN8nWebhook();
  const { brand } = useBrand();
  const { success, error } = useToast();
  const [context, setContext] = useState('');
  const [date, setDate] = useState(todayIso());

  const reset = () => {
    setContext('');
    setDate(todayIso());
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const generateUrl = createEmailUrl();
    if (!generateUrl) {
      error('n8n server URL is not configured (VITE_N8N_URL).');
      return;
    }
    const ok = await triggerWebhook(generateUrl, 'Tips email generation', {
      // One workflow serves every brand (docker/n8n-workflows/best-tips-email);
      // it generates for the brand this console is acting as.
      brand_key: brand.brandKey,
      additional_context: context,
      date: date || todayIso(),
      timestamp: new Date().toISOString(),
    });
    if (ok) success('Tips email generation started.');
    else error('Failed to start tips email generation. Please try again.');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Generate Tips Email
        </CardTitle>
        <CardDescription>Configure parameters for the daily tips email generated through the n8n workflow.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="tips-email-context" className="text-sm font-medium">
              Additional context
            </label>
            <textarea
              id="tips-email-context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              rows={4}
              placeholder="Enter any extra context for the email (special promotions, important announcements, specific race meetings to highlight…)"
              className={`${inputClass} resize-none`}
            />
            <p className="mt-1 text-xs text-muted-foreground">Included in the generation prompt to make the email more relevant.</p>
          </div>

          <div className="max-w-xs">
            <label htmlFor="tips-email-date" className="text-sm font-medium">
              Scheduled date
            </label>
            <input id="tips-email-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
            <p className="mt-1 text-xs text-muted-foreground">The date the email content should be prepared for.</p>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={reset} disabled={isLoading}>
              Reset
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Generate emails
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
